import { Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RuleIcon from '@mui/icons-material/Rule';
import { MRT_GlobalFilterTextField as GlobalFilterTextField } from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import ModalDuplicate from '@shared/components/modals/ModalDuplicate';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectAddTestCaseErrorInfo,
    selectAddTestCaseStatus,
    selectDeleteTestCaseErrorInfo,
    selectDeleteTestCaseStatus,
    selectDuplicateTestCaseStatus,
    selectTestCases,
} from '@pages/testcases/store/selectors';
import {
    addTestCaseReset,
    deleteTestCaseRequest,
    deleteTestCaseReset,
    duplicateTestCaseRequest,
    duplicateTestCaseReset,
} from '@pages/testcases/store';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import ModalSuccessContainer from '@shared/components/modals/ModalSuccessContainer';
import { UI_FUNCTIONAL_TESTING_PATH, UI_TEST_CASE_PATH } from '@shared/constants/pathsConstants';
import ModalDelete from '@shared/components/modals/ModalDelete';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { TESTCASE_STATUS } from '@pages/testcases/constants/testcasesСonstants';
import IncludeToTestPlanModal from './IncludeToTestPlanModal';
import { selectModules, selectModulesLookup } from '@pages/modules/store/selectors';
import HistoryIcon from '@mui/icons-material/History';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { selectGetTagsStatus, selectTags } from '@features/tags/store/selectors';
import UtmsTableToolbar from '@shared/components/ui/UtmsTableToolbar';
import { ModulesTreeViewSelect } from '@pages/modules';
import { TagSelector } from '@features/tags';
import DeployRunModalForTests from '@pages/stands/components/DeployModal/DeployRunModalForTests';
import { selectDeployEntityError, selectDeployEntityStatus } from '@pages/stands/store/selectors';
import { deployEntityReset } from '@pages/stands/store';

const TestcasesTableToolbar = ({
    table,
    selectedTestCaseId,
    setSelectedTestCaseId,
    selectedTestPlanId,
    setSelectedTestPlanId,
    moduleSelector = true,
    moduleIdForNavigateState,
    moduleNameFromUrlParams,
    setModuleNameFromUrlParams,
    tagsSearch,
    setTagsSearch,
}) => {
    const { t } = useTranslation();
    const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openIncludeToTestPlanModal, setOpenIncludeToTestPlanModal] = useState(false);
    const dispatch = useDispatch();
    const duplicateTestCaseStatus = useSelector(selectDuplicateTestCaseStatus);
    const deleteTestCaseStatus = useSelector(selectDeleteTestCaseStatus);
    const navigate = useNavigate();
    const [openTestcaseRunModal, setOpenTestcaseRunModal] = useState(false);
    const testCases = useSelector(selectTestCases);
    const selectedTestCaseFullObject =
        testCases && testCases.find((testCase) => testCase.pk === Number(selectedTestCaseId));
    const [selectedModuleId, setSelectedModuleId] = useState(null);
    const modules = useSelector(selectModules);
    const modulesLookup = useSelector(selectModulesLookup);
    const addTestcaseStatus = useSelector(selectAddTestCaseStatus);
    const [tagSelectorData, setTagSelectorData] = useState({ tags: [] });
    const getTagsStatus = useSelector(selectGetTagsStatus);
    const tags = useSelector(selectTags);
    const deployEntityStatus = useSelector(selectDeployEntityStatus);

    useEffect(() => {
        if (moduleNameFromUrlParams && modules.length) {
            const module = modules.find((item) => item.path === moduleNameFromUrlParams);
            if (module) {
                setSelectedModuleId(module.pk);
            }
        }
    }, [moduleNameFromUrlParams, modules]);

    const handleModuleChange = (newModuleId) => {
        setModuleNameFromUrlParams(modulesLookup[newModuleId]?.path || null);
        setSelectedModuleId(newModuleId || null);
    };

    const handleOpenDuplicateModal = () => {
        setOpenDuplicateModal(true);
    };

    const handleOpenDeleteModal = () => {
        setOpenDeleteModal(true);
    };

    const handleCloneTestCaseSubmit = (newName) => {
        dispatch(duplicateTestCaseRequest({ id: selectedTestCaseId, name: newName }));
    };

    const handleEditTestCase = () => {
        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${selectedTestCaseId}`);
    };

    const handleDeleteTestCaseSubmit = () => {
        dispatch(deleteTestCaseRequest({ id: selectedTestCaseId }));
        setSelectedTestCaseId(null);
    };

    const handleChangeTags = (newTagsData) => {
        setTagSelectorData(newTagsData);

        let newValue;
        if (typeof newTagsData === 'function') {
            newValue = newTagsData(tagSelectorData);
        } else {
            newValue = newTagsData;
        }

        setTagsSearch(newValue.tags.map((tag) => tag.pk));
    };

    useEffect(() => {
        if (getTagsStatus === REQUEST_STATUS.SUCCESS) {
            setTagSelectorData({ tags: tags.filter((tag) => tag.pk && tagsSearch.includes(tag.pk)) });
        }
    }, [getTagsStatus, tags, tagsSearch]);

    return (
        <Box>
            {selectedTestCaseFullObject && (
                <>
                    <ModalDuplicate
                        open={openDuplicateModal}
                        setOpen={setOpenDuplicateModal}
                        initialName={selectedTestCaseFullObject.name || ''}
                        object={t('test_case')}
                        title={`${t('duplicate')} ${t('test_case').toLowerCase()}`}
                        label={t('new_test_case_name')}
                        handleSubmit={handleCloneTestCaseSubmit}
                    />
                    <ModalDelete
                        open={openDeleteModal}
                        setOpen={setOpenDeleteModal}
                        objectName={`${t('test_case').toLocaleLowerCase()} "${selectedTestCaseFullObject.name}"`}
                        handleSubmit={handleDeleteTestCaseSubmit}
                    />
                </>
            )}

            {duplicateTestCaseStatus === REQUEST_STATUS.SUCCESS && (
                <ModalSuccessContainer
                    object={t('test_case')}
                    action={t('cloned')}
                    resetActionCallback={duplicateTestCaseReset}
                />
            )}
            {duplicateTestCaseStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={t('test_case').toLowerCase()}
                    action={t('cloned')}
                    resetActionCallback={duplicateTestCaseReset}
                    errorSelector={selectDuplicateTestCaseStatus}
                />
            )}
            {deleteTestCaseStatus === REQUEST_STATUS.SUCCESS && (
                <ModalSuccessContainer
                    object={t('test_case')}
                    action={t('deleted')}
                    resetActionCallback={deleteTestCaseReset}
                />
            )}
            {deleteTestCaseStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={t('test_case').toLowerCase()}
                    action={t('deleting')}
                    resetActionCallback={deleteTestCaseReset}
                    errorSelector={selectDeleteTestCaseErrorInfo}
                />
            )}
            {addTestcaseStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={t('test_case_r')}
                    action={t('creating')}
                    resetActionCallback={addTestCaseReset}
                    errorSelector={selectAddTestCaseErrorInfo}
                />
            )}
            {addTestcaseStatus === REQUEST_STATUS.SUCCESS && (
                <ModalSuccessContainer
                    object={t('test_case')}
                    action={t('created')}
                    resetActionCallback={addTestCaseReset}
                />
            )}
            {deployEntityStatus === REQUEST_STATUS.ERROR && (
                <ModalErrorContainer
                    object={t('test_case_r')}
                    action={t('starting_r')}
                    resetActionCallback={deployEntityReset}
                    errorSelector={selectDeployEntityError}
                />
            )}
            <IncludeToTestPlanModal
                open={openIncludeToTestPlanModal}
                setOpen={setOpenIncludeToTestPlanModal}
                selectedTestPlanId={selectedTestPlanId}
                setSelectedTestPlanId={setSelectedTestPlanId}
                selectedTestCaseId={selectedTestCaseId}
            />
            <DeployRunModalForTests
                open={openTestcaseRunModal}
                setOpen={setOpenTestcaseRunModal}
                entityType="testcase"
                entity={selectedTestCaseFullObject}
            />
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    mb: 2,
                }}
            >
                <UtmsButton
                    startIcon={<AddIcon />}
                    onClick={() => {
                        dispatch(addTestCaseReset());
                        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/create`, {
                            state: {
                                module: moduleIdForNavigateState,
                            },
                        });
                    }}
                >
                    {t('add')}
                </UtmsButton>
                <UtmsButton
                    startIcon={<ContentCopyIcon />}
                    disabled={!selectedTestCaseId}
                    onClick={handleOpenDuplicateModal}
                >
                    {t('clone')}
                </UtmsButton>
                <UtmsButton startIcon={<EditIcon />} disabled={!selectedTestCaseId} onClick={handleEditTestCase}>
                    {t('edit')}
                </UtmsButton>
                <UtmsButton
                    startIcon={<RuleIcon />}
                    disabled={!selectedTestCaseId}
                    onClick={() => setOpenIncludeToTestPlanModal(true)}
                >
                    {t('include_to_test_plan')}
                </UtmsButton>
                <Divider orientation="vertical" flexItem />
                <UtmsButton
                    startIcon={<PlayArrowIcon />}
                    disabled={!(selectedTestCaseId && selectedTestCaseFullObject?.status === TESTCASE_STATUS.APPROVED)}
                    onClick={() => setOpenTestcaseRunModal(true)}
                >
                    {t('run')}
                </UtmsButton>
                <Divider orientation="vertical" flexItem />
                <UtmsButton
                    startIcon={<DeleteOutlineIcon />}
                    disabled={!selectedTestCaseId}
                    onClick={handleOpenDeleteModal}
                >
                    {t('delete')}
                </UtmsButton>
                <Divider orientation="vertical" flexItem />
                <UtmsButton
                    disabled={!selectedTestCaseId}
                    onClick={() =>
                        navigate(`/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}/${selectedTestCaseId}/history`)
                    }
                    startIcon={<HistoryIcon />}
                >
                    {t('history')}
                </UtmsButton>
            </Box>
            <UtmsTableToolbar
                table={table}
                enableGlobalFilterTextField
                enableToggleFiltersButton
                enableShowHideColumnsButton
                GlobalFilterTextFieldComponent={
                    <GlobalFilterTextField
                        table={table}
                        sx={{ width: 200 }}
                        inputProps={{
                            sx: { height: 25 },
                            placeholder: t('test_case') + '...',
                        }}
                    />
                }
                ModuleSelectorComponent={
                    moduleSelector ? (
                        <ModulesTreeViewSelect
                            value={selectedModuleId}
                            onChange={handleModuleChange}
                            inputProps={{
                                sx: {
                                    width: 294,
                                    height: 42,
                                },
                            }}
                            selectPaperStyle={{
                                width: 276,
                            }}
                        />
                    ) : undefined
                }
                TagSelectorComponent={
                    <Box sx={{ width: 300, height: 42, position: 'relative' }}>
                        <TagSelector
                            readonly
                            data={tagSelectorData}
                            setData={handleChangeTags}
                            limitTags={2}
                            entityType="testcases"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                            }}
                        />
                    </Box>
                }
            />
        </Box>
    );
};

export default TestcasesTableToolbar;
